import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Hash,
  Edit3,
  Eye,
  Scan,
} from 'lucide-react';
import VinScannerScreen from '../calls/VinScannerScreen';
import { useDriverImpound } from '@/hooks/useDriverImpound';
import { useHybridAuth } from '@/hooks/useHybridAuth';
import { mapDriverImpoundToImpound } from '@/types';
import { useUpdateVehicle } from '@/hooks/useUpdateVehicle';
import { useVehicleImages } from '@/hooks/useVehicleImages';
import { usePlan } from '@/hooks/usePlan';
import { useHasPermission } from '@/hooks/useDriverPermissions';
import VehicleNotesSection from '@/components/common/VehicleNotesSection';
import { useGetVehicleMakes } from '@/api/hooks/useGetVehicleMakes';
import { useGetVehicleModels } from '@/api/hooks/useGetVehicleModels';
import { isEmbeddedMode } from '@/utils/embeddedMode';
import SlideShowModal from '@/components/common/SlideShowModal';
import VehicleMediaCarousel from '@/components/common/VehicleMediaCarousel';
import { useVehicleMediaUpload } from '@/hooks/useVehicleMediaUpload';

interface ImpoundScreenProps {
  impoundId: string;
  onBack: () => void;
  showHeader?: boolean;
}

const ImpoundScreen: React.FC<ImpoundScreenProps> = ({
  impoundId,
  onBack,
  showHeader = true,
}) => {
  const {
    data: driverImpoundData,
    isPending,
    error,
  } = useDriverImpound(impoundId);
  const { isLoading: authLoading } = useHybridAuth();
  /** Auth resolving or query idle/disabled without data (v5: isLoading is only true while fetching). */
  const isImpoundStateLoading = authLoading || isPending;
  const updateVehicleMutation = useUpdateVehicle();

  // Check if we're in embedded mode
  const isEmbedded = isEmbeddedMode();

  // Vehicle images carousel state
  const {
    vehicleImages = [],
    isLoading: isImagesLoading,
    uploadImage,
    deleteImage,
  } = useVehicleImages(driverImpoundData?.dispatch?.vehicle?.id);

  // Video is a plan feature, of the selected company's plan.
  const { includes, isLoading: isVideoPermissionsLoading } = usePlan();
  const canUploadVideo = includes('video');

  const { uploadProgress, uploadingFilesCount, handleTakePhoto } =
    useVehicleMediaUpload({
      vehicleId: driverImpoundData?.dispatch?.vehicle?.id,
      uploadImage,
    });

  // Form state for editable fields
  const [isEditing, setIsEditing] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const [formData, setFormData] = React.useState({
    vehicleName: '',
    vehicleModel: '',
    vehicleYear: 0,
    vehicleColor: '',
    vehicleOdometer: null as number | null,
    licensePlate: '',
    vin: '',
  });

  const [showVinScanner, setShowVinScanner] = useState(false);
  const [scannedVin, setScannedVin] = useState<string | null>(null);
  const [showSlideShow, setShowSlideShow] = useState(false);
  const [slideShowIndex, setSlideShowIndex] = useState(0);

  // Vehicle makes and models data — only fetch when user enters edit mode
  const { data: vehicleMakesData } = useGetVehicleMakes(undefined, {
    enabled: isEditing,
  });
  const { data: vehicleModelsData } = useGetVehicleModels(
    { make: formData.vehicleName },
    { enabled: !!formData.vehicleName }
  );

  const canEdit = useHasPermission('impounds.edit');

  // Initialize form data when impound data loads
  React.useEffect(() => {
    if (driverImpoundData) {
      const impound = mapDriverImpoundToImpound(driverImpoundData);
      setFormData({
        vehicleName: impound.vehicleMake,
        vehicleModel: impound.vehicleModel,
        vehicleYear: impound.vehicleYear,
        vehicleColor: impound.vehicleColor,
        vehicleOdometer: impound.vehicleOdometer,
        licensePlate: impound.licensePlate,
        vin: impound.vin,
      });
    }
  }, [driverImpoundData]);

  const handleInputChange = (field: string, value: string | number | null) => {
    // Auto-capitalize VIN and license plate
    if (
      (field === 'vin' || field === 'licensePlate') &&
      typeof value === 'string'
    ) {
      value = value.toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!driverImpoundData?.dispatch?.vehicle?.id) return;

    try {
      setSaveError(null);
      const vehicleAttributes = {
        color: formData.vehicleColor,
      };

      await updateVehicleMutation.mutateAsync({
        vehicleId: driverImpoundData.dispatch.vehicle.id,
        updateData: {
          vehicleName: formData.vehicleName,
          vehicleModel: formData.vehicleModel,
          vehicleYear: formData.vehicleYear,
          licensePlate: formData.licensePlate,
          vin: formData.vin,
          odometer: formData.vehicleOdometer || undefined,
          vehicleAttributes,
        },
      });

      setHasChanges(false);
      setIsEditing(false);
      setSaveSuccess(true);

      // Clear success message after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error: any) {
      console.error('Failed to update vehicle:', error);
      setSaveError(error.message || 'Failed to save changes');
    }
  };

  const handleCancel = () => {
    if (driverImpoundData) {
      const impound = mapDriverImpoundToImpound(driverImpoundData);
      setFormData({
        vehicleName: impound.vehicleMake,
        vehicleModel: impound.vehicleModel,
        vehicleYear: impound.vehicleYear,
        vehicleColor: impound.vehicleColor,
        vehicleOdometer: impound.vehicleOdometer,
        licensePlate: impound.licensePlate,
        vin: impound.vin,
      });
    }
    setHasChanges(false);
    setIsEditing(false);
    setScannedVin(null); // Reset scanned VIN when cancelling
  };

  const handleScanVin = () => {
    console.log('Scan VIN clicked');
    setShowVinScanner(true);
  };

  const handleRescanVin = () => {
    console.log('Rescan VIN clicked');
    setShowVinScanner(true);
  };

  const handleVinScannerClose = () => {
    setShowVinScanner(false);
  };

  const handleVinScanned = (vin: string) => {
    setScannedVin(vin);
    setFormData((prev) => ({ ...prev, vin }));
    setHasChanges(true);
    setSaveError(null);
    setSaveSuccess(false);
    setShowVinScanner(false);
  };

  const removePhoto = async (index: number) => {
    const imageUrl = vehicleImages[index];
    if (!imageUrl || !driverImpoundData?.dispatch?.vehicle?.id) return;

    try {
      await deleteImage.mutateAsync({
        vehicleId: driverImpoundData.dispatch.vehicle.id,
        imageUrl,
      });
    } catch (error) {
      console.error('Failed to delete image:', error);
    }
  };

  // Set up Android navigation callbacks
  useEffect(() => {
    // Only set up navigation in embedded mode
    if (typeof (window as any).ReactNativeWebView !== 'undefined') {
      console.log('📱 [ImpoundScreen] Setting up Android navigation callbacks');

      (window as any).navigateBack = function () {
        console.log(
          '📱 [ImpoundScreen] navigateBack called - going back to HomeScreen'
        );
        onBack();
      };

      (window as any).navigateForward = function () {
        console.log(
          '📱 [ImpoundScreen] navigateForward called - no action (forward not applicable)'
        );
        // Do nothing - forward navigation not applicable on ImpoundScreen
      };

      // Cleanup function
      return () => {
        console.log(
          '📱 [ImpoundScreen] Cleaning up Android navigation callbacks'
        );
        delete (window as any).navigateBack;
        delete (window as any).navigateForward;
      };
    }
  }, [onBack]);

  if (isImpoundStateLoading) {
    return (
      <div className="min-h-screen bg-primary responsive-container mx-auto flex flex-col">
        {/* Header */}
        {showHeader && (
          <div className="p-4 flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-background/10 rounded-lg transition-colors"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>
            <h1 className="text-xl font-semibold text-white">
              Impound Details
            </h1>
          </div>
        )}

        {/* Loading state */}
        <div className="bg-background flex-1 rounded-t-3xl">
          <div className="p-6 flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-600">Loading impound details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show VinScannerScreen if scanner is active
  if (showVinScanner) {
    return (
      <VinScannerScreen
        onClose={handleVinScannerClose}
        onVinScanned={handleVinScanned}
        vehicleId={driverImpoundData?.dispatch?.vehicle?.id || ''}
      />
    );
  }

  if (error || !driverImpoundData) {
    return (
      <div className="min-h-screen bg-primary responsive-container mx-auto flex flex-col">
        {/* Header */}
        {showHeader && (
          <div className="p-4 flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-background/10 rounded-lg transition-colors"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>
            <h1 className="text-xl font-semibold text-white">
              Impound Details
            </h1>
          </div>
        )}

        {/* Error state */}
        <div className="bg-background flex-1 rounded-t-3xl">
          <div className="p-6 flex items-center justify-center min-h-96">
            <div className="text-center">
              <p className="text-red-600 text-lg mb-2">
                Failed to load impound
              </p>
              <p className="text-gray-600 text-sm">Please try again later</p>
              <button
                onClick={onBack}
                className="mt-4 bg-primary text-primary-foreground px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const impound = mapDriverImpoundToImpound(driverImpoundData);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatVehicleDescription = (
    year: number,
    make: string,
    model: string
  ) => {
    // If both make and model are not provided, show "Unspecified Vehicle"
    if (
      (!make || make === 'null' || make === 'unknown' || make === 'Unknown') &&
      (!model || model === 'null' || model === 'unknown' || model === 'Unknown')
    ) {
      return 'Unspecified Vehicle';
    }

    // Build the description parts
    const parts: string[] = [];

    // Add year only if it's valid (not 0, null, or blank)
    if (year && year > 0) {
      parts.push(year.toString());
    }

    // Add make if provided
    if (make && make !== 'null' && make !== 'unknown' && make !== 'Unknown') {
      parts.push(make);
    }

    // Add model if provided
    if (
      model &&
      model !== 'null' &&
      model !== 'unknown' &&
      model !== 'Unknown'
    ) {
      parts.push(model);
    }

    return parts.join(' ');
  };

  return (
    <div className="min-h-screen bg-primary responsive-container mx-auto flex flex-col">
      {/* Header */}
      {showHeader && (
        <div className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-background/10 rounded-lg transition-colors"
            >
              <ArrowLeft size={24} className="text-white" />
            </button>
            <h1 className="text-xl font-semibold text-white">Back</h1>
          </div>

          {/* Save Button - appears when there are changes */}
          {hasChanges && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleCancel}
                className="px-3 py-1 text-sm text-white border border-white border-opacity-50 rounded-lg hover:bg-background/10 transition-colors"
                disabled={updateVehicleMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm bg-background text-primary rounded-lg font-medium hover:bg-gray-100 transition-colors disabled:opacity-50"
                disabled={updateVehicleMutation.isPending}
              >
                {updateVehicleMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Content */}
      <div
        className={`bg-background flex-1 rounded-t-3xl overflow-y-auto ${
          isEmbedded
            ? 'min-h-[600px]' // Fixed height for WebView compatibility
            : '' // No fixed height for normal web mode
        }`}
      >
        <div className="px-4 py-4 space-y-6">
          {/* Vehicle Photos Carousel */}
          <VehicleMediaCarousel
            vehicleImages={vehicleImages}
            isLoading={isImagesLoading}
            uploadProgress={uploadProgress}
            uploadingFilesCount={uploadingFilesCount}
            isVideoPermissionsLoading={isVideoPermissionsLoading}
            canUploadVideo={canUploadVideo}
            onImageClick={(index) => {
              setSlideShowIndex(index);
              setShowSlideShow(true);
            }}
            onTakePhoto={handleTakePhoto}
            uploadButtonText={canUploadVideo ? 'Add Media' : 'Add Photo'}
            screenName="ImpoundScreen"
          />

          {/* Vehicle Header */}
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-bold text-black">
              Impound# {impound.impoundNumber}
            </h2>
            <div className="flex-1"></div>
            <p className="text-gray-600 text-sm">
              {impound.vehicleMake || 'Unspecified Vehicle'}
            </p>
          </div>

          {/* Vehicle Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-black">
                Vehicle Information
              </h3>
              {canEdit && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className="flex items-center gap-1 text-sm text-primary hover:text-opacity-80 transition-colors"
                >
                  {isEditing ? (
                    <>
                      <Eye size={14} />
                      View
                    </>
                  ) : (
                    <>
                      <Edit3 size={14} />
                      Edit
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Success/Error Messages */}
            {saveSuccess && (
              <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">
                  ✓ Vehicle information updated successfully
                </p>
              </div>
            )}

            {saveError && (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700">✗ {saveError}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">Make</p>
                {isEditing ? (
                  <select
                    value={
                      vehicleMakesData?.find(
                        (make) =>
                          make.make.toLowerCase().trim() ===
                          formData.vehicleName?.toLowerCase().trim()
                      )?.make || formData.vehicleName
                    }
                    onChange={(e) => {
                      handleInputChange('vehicleName', e.target.value);
                      // Reset model when make changes
                      handleInputChange('vehicleModel', '');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">Select Make</option>
                    {vehicleMakesData?.map((make) => (
                      <option key={make.make} value={make.make}>
                        {make.make}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="font-medium text-black">
                    {formData.vehicleName}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Model</p>
                {isEditing ? (
                  <select
                    value={
                      vehicleModelsData?.find(
                        (model) =>
                          model.model.toLowerCase().trim() ===
                          formData.vehicleModel?.toLowerCase().trim()
                      )?.model || formData.vehicleModel
                    }
                    onChange={(e) =>
                      handleInputChange('vehicleModel', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                    disabled={!formData.vehicleName}
                  >
                    <option value="">Select Model</option>
                    {vehicleModelsData?.map((model) => (
                      <option key={model.id} value={model.model}>
                        {model.model}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="font-medium text-black">
                    {formData.vehicleModel}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Year</p>
                {isEditing ? (
                  <input
                    type="number"
                    value={formData.vehicleYear}
                    onChange={(e) =>
                      handleInputChange(
                        'vehicleYear',
                        parseInt(e.target.value) || 0
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                ) : (
                  <p className="font-medium text-black">
                    {formData.vehicleYear}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Color</p>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.vehicleColor}
                    onChange={(e) =>
                      handleInputChange('vehicleColor', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                ) : (
                  <p className="font-medium text-black">
                    {formData.vehicleColor}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">License Plate</p>
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.licensePlate}
                    onChange={(e) =>
                      handleInputChange('licensePlate', e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                ) : (
                  <p className="font-medium text-black">
                    {formData.licensePlate}
                  </p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">Odometer</p>
                {isEditing ? (
                  <input
                    type="number"
                    value={formData.vehicleOdometer || ''}
                    onChange={(e) =>
                      handleInputChange(
                        'vehicleOdometer',
                        e.target.value ? parseInt(e.target.value) : null
                      )
                    }
                    placeholder="Miles"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                ) : (
                  <p className="font-medium text-black">
                    {formData.vehicleOdometer
                      ? formData.vehicleOdometer.toLocaleString()
                      : 'Not set'}
                  </p>
                )}
              </div>
            </div>

            <div className="pt-2">
              <p className="text-sm text-gray-500 mb-1">Vehicle VIN</p>
              <div className="flex items-center gap-3">
                {isEditing ? (
                  <input
                    type="text"
                    value={formData.vin}
                    onChange={(e) => handleInputChange('vin', e.target.value)}
                    placeholder="Enter 17-character VIN"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-900 bg-background focus:ring-2 focus:ring-primary focus:border-primary font-mono placeholder-gray-400"
                  />
                ) : (
                  <div className="flex-1 bg-background border border-gray-300 rounded-md p-3">
                    <p className="font-mono text-sm text-gray-900 font-medium">
                      {scannedVin || formData.vin || 'No VIN available'}
                    </p>
                  </div>
                )}
                {/* Scan VIN button - always visible */}
                {scannedVin || formData.vin ? (
                  <button
                    onClick={handleRescanVin}
                    className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground text-xs rounded-md hover:bg-primary/90 transition-colors"
                  >
                    <Scan size={12} />
                    {isEditing ? 'Rescan' : 'Rescan'}
                  </button>
                ) : (
                  <button
                    onClick={handleScanVin}
                    className="flex items-center gap-1 px-3 py-2 bg-primary text-primary-foreground text-xs rounded-md hover:bg-primary/90 transition-colors"
                  >
                    <Scan size={12} />
                    {isEditing ? 'Scan' : 'Scan VIN'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Impound Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-black">
              Impound Details
            </h3>

            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Calendar size={20} className="text-gray-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Impounded</p>
                  <p className="font-medium text-black">
                    {formatDate(impound.impoundDate)} •{' '}
                    {formatTime(impound.impoundDate)}
                  </p>
                </div>
              </div>

              {impound.status === 'RELEASED' && (
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Calendar size={20} className="text-gray-600 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm text-gray-500">Released</p>
                    <p className="font-medium text-black">
                      {impound.releaseAt
                        ? `${formatDate(impound.releaseAt)} • ${formatTime(impound.releaseAt)}`
                        : '—'}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin size={20} className="text-gray-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium text-black">
                    {impound.lotLocation}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <Hash size={20} className="text-gray-600 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm text-gray-500">Vehicle VIN</p>
                  <p className="font-medium text-black break-all">
                    {impound.vin}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          {(impound.total > 0 || impound.balanceDue > 0) && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-black">Financial</h3>

              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total</span>
                  <span className="font-semibold text-black">
                    {formatCurrency(impound.total)}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Balance Due</span>
                  <span className="font-semibold text-black">
                    {formatCurrency(impound.balanceDue)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                  <span className="text-gray-600">Days Held</span>
                  <span className="font-semibold text-black">
                    {impound.daysHeld} days
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Vehicle Notes Section */}
          <VehicleNotesSection
            vehicleId={driverImpoundData?.dispatch?.vehicle?.id}
            onError={(error) => setSaveError(error)}
          />

          {/* Bottom Padding */}
          <div className="h-6"></div>
        </div>
      </div>

      {/* SlideShowModal */}
      <SlideShowModal
        isOpen={showSlideShow}
        onClose={() => setShowSlideShow(false)}
        images={vehicleImages}
        initialIndex={slideShowIndex}
        onDelete={(index) => removePhoto(index)}
        isDeleting={deleteImage.isPending}
        vehicleId={driverImpoundData?.dispatch?.vehicle?.id}
      />
    </div>
  );
};

export default ImpoundScreen;
